import DashboardLayout from "@/components/DashboardLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { CheckCircle, Circle, Clock, ChevronDown, ChevronRight, Zap } from "lucide-react";
import { useState } from "react";

type TaskStatus = "done" | "in_progress" | "pending";

type Task = {
  id: string;
  title: string;
  status: TaskStatus;
};

type Phase = {
  id: string;
  number: number;
  title: string;
  description: string;
  status: TaskStatus;
  tasks: Task[];
};

const phases: Phase[] = [
  {
    id: "p1", number: 1, title: "Mapeamento e Planejamento", status: "done",
    description: "Análise do ManyChat e definição da arquitetura do sistema.",
    tasks: [
      { id: "t1-1", title: "Exploração do ManyChat (broadcasts, templates, contatos)", status: "done" },
      { id: "t1-2", title: "Mapeamento da estrutura de disparos e templates complexos", status: "done" },
      { id: "t1-3", title: "Definição da arquitetura da solução customizada", status: "done" },
      { id: "t1-4", title: "Levantamento de requisitos (campos de contato, dashboard, automações)", status: "done" },
    ],
  },
  {
    id: "p2", number: 2, title: "Backend e Banco de Dados", status: "done",
    description: "Estrutura de dados e API tRPC para suportar toda a lógica de negócio.",
    tasks: [
      { id: "t2-1", title: "Schema completo (contacts, tags, templates, campaigns, automations, settings, logs)", status: "done" },
      { id: "t2-2", title: "Helpers de consulta em server/db.ts (CRUD + filtros + paginação)", status: "done" },
      { id: "t2-3", title: "Endpoints tRPC (tags, contacts, templates, campaigns, automations, settings, dashboard, search)", status: "done" },
      { id: "t2-4", title: "Migração do banco de dados (pnpm db:push)", status: "done" },
      { id: "t2-5", title: "Testes unitários dos endpoints (16 testes passando)", status: "done" },
    ],
  },
  {
    id: "p3", number: 3, title: "Frontend — Interface Admin", status: "done",
    description: "Todas as telas do painel administrativo.",
    tasks: [
      { id: "t3-1", title: "Tema visual (verde WhatsApp + sidebar dark)", status: "done" },
      { id: "t3-2", title: "Dashboard com métricas e ações rápidas", status: "done" },
      { id: "t3-3", title: "Gerenciador de Contatos com importação CSV, exportação e filtro por tag", status: "done" },
      { id: "t3-4", title: "Perfil detalhado do contato com histórico de mensagens", status: "done" },
      { id: "t3-5", title: "Gerenciador de Tags com cores personalizáveis", status: "done" },
      { id: "t3-6", title: "Editor de Templates com preview WhatsApp e variáveis dinâmicas", status: "done" },
      { id: "t3-7", title: "Gerenciador de Campanhas com agendamento e duplicação", status: "done" },
      { id: "t3-8", title: "Relatório individual por campanha (detalhes por contato)", status: "done" },
      { id: "t3-9", title: "Motor de Automações com 6 gatilhos e 5 ações", status: "done" },
      { id: "t3-10", title: "Histórico de execuções de automações", status: "done" },
      { id: "t3-11", title: "Caixa de Mensagens Recebidas", status: "done" },
      { id: "t3-12", title: "Relatórios/Analytics com gráficos (Recharts)", status: "done" },
      { id: "t3-13", title: "Opt-in via Link público de cadastro", status: "done" },
      { id: "t3-14", title: "Busca Global no header (contatos, templates, campanhas)", status: "done" },
      { id: "t3-15", title: "Página de Configurações (API Meta + Teste de Conexão)", status: "done" },
    ],
  },
  {
    id: "p4", number: 4, title: "Integração Meta API (WhatsApp)", status: "done",
    description: "Conexão do sistema à API oficial do WhatsApp Business.",
    tasks: [
      { id: "t4-1", title: "Serviço de disparo real via API Meta (whatsapp.service.ts)", status: "done" },
      { id: "t4-2", title: "Disparo com variáveis dinâmicas ({{1}} = nome, {{2}} = empresa)", status: "done" },
      { id: "t4-3", title: "Webhook de status de entrega (/api/webhook/whatsapp)", status: "done" },
      { id: "t4-4", title: "Sincronização de templates aprovados com a Meta", status: "done" },
      { id: "t4-5", title: "Opt-out automático ao receber 'stop' ou 'parar'", status: "done" },
      { id: "t4-6", title: "Endpoint de teste de conexão com a API Meta", status: "done" },
    ],
  },
  {
    id: "p5", number: 5, title: "Motor de Automações", status: "done",
    description: "Sistema inteligente de gatilhos e ações automáticas.",
    tasks: [
      { id: "t5-1", title: "Gatilho: novo_contato (dispara ao cadastrar contato)", status: "done" },
      { id: "t5-2", title: "Gatilho: palavra_chave (dispara ao receber mensagem)", status: "done" },
      { id: "t5-3", title: "Gatilho: tag_adicionada / tag_removida", status: "done" },
      { id: "t5-4", title: "Gatilho: inatividade (X dias sem interação)", status: "done" },
      { id: "t5-5", title: "Gatilho: agendado (data/hora específica)", status: "done" },
      { id: "t5-6", title: "Ação: enviar_template, adicionar_tag, remover_tag, notificar_admin", status: "done" },
      { id: "t5-7", title: "Agendador automático (roda a cada minuto)", status: "done" },
      { id: "t5-8", title: "Log de execuções com status de sucesso/falha", status: "done" },
    ],
  },
  {
    id: "p6", number: 6, title: "Configuração e Ativação Meta", status: "in_progress",
    description: "Último passo: inserir credenciais reais e fazer o primeiro disparo.",
    tasks: [
      { id: "t6-1", title: "Inserir Phone Number ID nas Configurações", status: "pending" },
      { id: "t6-2", title: "Inserir Access Token permanente nas Configurações", status: "pending" },
      { id: "t6-3", title: "Inserir Verify Token e configurar webhook na Meta", status: "pending" },
      { id: "t6-4", title: "Testar conexão com a API Meta", status: "pending" },
      { id: "t6-5", title: "Sincronizar templates aprovados da conta Meta", status: "pending" },
      { id: "t6-6", title: "Importar base de contatos via CSV", status: "pending" },
      { id: "t6-7", title: "Criar tags de segmentação (ex: Contadores-MT, Interesse-Reforma)", status: "pending" },
      { id: "t6-8", title: "Disparar primeira campanha de teste", status: "pending" },
    ],
  },
];

function getPhaseProgress(phase: Phase) {
  const done = phase.tasks.filter(t => t.status === "done").length;
  return Math.round((done / phase.tasks.length) * 100);
}

function getTotalProgress() {
  const allTasks = phases.flatMap(p => p.tasks);
  const done = allTasks.filter(t => t.status === "done").length;
  return Math.round((done / allTasks.length) * 100);
}

function StatusIcon({ status }: { status: TaskStatus }) {
  if (status === "done") return <CheckCircle className="h-4 w-4 text-green-600 shrink-0" />;
  if (status === "in_progress") return <Zap className="h-4 w-4 text-yellow-500 shrink-0" />;
  return <Circle className="h-4 w-4 text-muted-foreground/40 shrink-0" />;
}

function PhaseBadge({ status }: { status: TaskStatus }) {
  if (status === "done") return <Badge className="bg-green-100 text-green-800 border-green-200 hover:bg-green-100">Concluída</Badge>;
  if (status === "in_progress") return <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200 hover:bg-yellow-100">Em andamento</Badge>;
  return <Badge variant="secondary">Pendente</Badge>;
}

export default function DevelopmentProgress() {
  const [expandedPhases, setExpandedPhases] = useState<Set<string>>(new Set(["p6"]));
  const totalProgress = getTotalProgress();

  const togglePhase = (id: string) => {
    setExpandedPhases(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const allTasks = phases.flatMap(p => p.tasks);
  const doneTasks = allTasks.filter(t => t.status === "done").length;
  const inProgressTasks = allTasks.filter(t => t.status === "in_progress").length;
  const pendingTasks = allTasks.filter(t => t.status === "pending").length;

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-3xl">
        <div>
          <h1 className="text-2xl font-bold">Progresso do Desenvolvimento</h1>
          <p className="text-muted-foreground text-sm mt-0.5">
            Acompanhe o status de cada fase da construção do sistema
          </p>
        </div>

        {/* Overall Progress */}
        <Card className="border-primary/20 bg-primary/5">
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="font-semibold text-lg">Progresso Geral</p>
                <p className="text-sm text-muted-foreground mt-0.5">
                  {doneTasks} de {allTasks.length} tarefas concluídas
                </p>
              </div>
              <div className="text-3xl font-bold text-primary">{totalProgress}%</div>
            </div>
            <Progress value={totalProgress} className="h-3" />
            <div className="flex gap-4 mt-3 text-xs text-muted-foreground">
              <span className="flex items-center gap-1"><CheckCircle className="h-3 w-3 text-green-600" /> {doneTasks} concluídas</span>
              <span className="flex items-center gap-1"><Zap className="h-3 w-3 text-yellow-500" /> {inProgressTasks} em andamento</span>
              <span className="flex items-center gap-1"><Clock className="h-3 w-3" /> {pendingTasks} pendentes</span>
            </div>
          </CardContent>
        </Card>

        {/* Phases */}
        <div className="space-y-3">
          {phases.map((phase) => {
            const progress = getPhaseProgress(phase);
            const isExpanded = expandedPhases.has(phase.id);

            return (
              <Card key={phase.id} className={phase.status === "done" ? "border-green-200/60" : phase.status === "in_progress" ? "border-yellow-300/60" : ""}>
                <CardContent className="p-0">
                  <button
                    className="w-full text-left p-4 flex items-start gap-3 hover:bg-muted/30 transition-colors rounded-lg"
                    onClick={() => togglePhase(phase.id)}
                  >
                    <div className="flex items-center justify-center w-7 h-7 rounded-full bg-primary/10 text-primary text-xs font-bold shrink-0 mt-0.5">
                      {phase.number}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-sm">{phase.title}</span>
                        <PhaseBadge status={phase.status} />
                        <span className="text-xs text-muted-foreground ml-auto">{progress}%</span>
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5">{phase.description}</p>
                      <Progress value={progress} className="h-1.5 mt-2" />
                    </div>
                    <div className="shrink-0 mt-1">
                      {isExpanded ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
                    </div>
                  </button>

                  {isExpanded && (
                    <div className="px-4 pb-4 space-y-1.5 border-t border-border/50 pt-3">
                      {phase.tasks.map((task) => (
                        <div key={task.id} className="flex items-start gap-2.5 py-1">
                          <StatusIcon status={task.status} />
                          <span className={`text-sm ${task.status === "done" ? "text-muted-foreground line-through" : task.status === "in_progress" ? "font-medium" : "text-foreground"}`}>
                            {task.title}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Next Step Highlight */}
        <Card className="border-yellow-300/60 bg-yellow-50/50 dark:bg-yellow-950/20">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <Zap className="h-5 w-5 text-yellow-600 shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-sm text-yellow-800 dark:text-yellow-400">Próximo Passo: Ativar a API Meta</p>
                <p className="text-xs text-yellow-700/80 dark:text-yellow-500/80 mt-1">
                  O sistema está 100% desenvolvido. Para começar a disparar mensagens reais, acesse <strong>Configurações → API Meta</strong>, insira seu Phone Number ID e Access Token, e clique em "Testar Conexão".
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
